import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  output,
} from '@angular/core';
import { CommunityItem, SocialMediaPlatform } from '../../../../interfaces';
import { TEXT_CONSTANTS } from '../../../../constances';
import { SliderComponent, SliderImage } from '../../../slider/slider.component';
import { YoutubeEmbedPipe } from '../../../../pipes/youtube-embed.pipe';
import { TiktokEmbedPipe } from '../../../../pipes/tiktok-embed.pipe';
import { UnixDateFormatterPipe } from '../../../../pipes/unix-date-formatter.pipe';
import { TextContentComponent } from '../text-content/text-content.component';
import { FeatureFlagService } from '../../../../services/util/feature-flag.service';
import { CommunityNavigationService } from '../../../../services/navigation/community-navigation.service';
import { TargetUrl } from '@outlaw/core';
import { ExternalLinkDirective } from '../../../../directives/external-link.directive';

@Component({
  selector: 'app-social-media-post',
  templateUrl: './social-media-post.component.html',
  styleUrls: ['./social-media-post.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SliderComponent, YoutubeEmbedPipe, TiktokEmbedPipe, UnixDateFormatterPipe, TextContentComponent, ExternalLinkDirective],
})
export class SocialMediaPostComponent implements OnInit, AfterViewInit, OnDestroy {
  item = input.required<CommunityItem>();
  variant = input<'primary' | 'secondary'>('primary');
  openInSameWindow = input<boolean>(false);
  isMajiApp = input<boolean>(false);

  navigateToPost = output<void>();

  postUrl = computed(() => {
    return this.variant() === 'primary' && !this.featureFlagService.isCommunityInApp()
      ? this.navigationService.getPostUrl(this.item().post_uuid)
      : null;
  });

  mappedImages = signal<SliderImage[]>([]);

  @ViewChild('videoElement') videoElement: ElementRef<HTMLVideoElement>;
  @ViewChild('videoVisibility') videoVisibility: ElementRef;

  isMuted = true;
  private observer: IntersectionObserver;
  private cdr = inject(ChangeDetectorRef);
  private featureFlagService = inject(FeatureFlagService);
  private navigationService = inject(CommunityNavigationService);

  get hasVideo(): boolean {
    return !!this.item().content.videos?.[0]?.video_url;
  }

  get youtubePost(): boolean {
    return this.item().original_platform.slug === SocialMediaPlatform.YouTube;
  }

  get tiktokPost(): boolean {
    return this.item().original_platform.slug === SocialMediaPlatform.TikTok;
  }

  get isInstagramPost(): boolean {
    return this.item().original_platform.slug === SocialMediaPlatform.Instagram;
  }

  readonly maxTextLength = TEXT_CONSTANTS.MAX_TEXT_LENGTH;
  readonly SocialMediaPlatform = SocialMediaPlatform;

  get platformSlug(): SocialMediaPlatform {
    return this.item().original_platform.slug.toLowerCase() as SocialMediaPlatform;
  }

  get userUrl(): string {
    return this.item().content.social_media?.external_user_url;
  }

  ngOnInit(): void {
    const content = this.item().content;

    const images =
      content.images && content.images.length > 0
        ? content.images.map(img => ({
            imageUrl: img.image_url,
            url: this.postUrl(),
            alt: img.image_url,
          }))
        : content.videos && content.videos.length > 0 && content.videos[0].preview_url
          ? [
              {
                imageUrl: content.videos[0].preview_url,
                url: this.postUrl(),
                alt: content.videos[0].preview_url,
              },
            ]
          : [];

    this.mappedImages.set(images);
  }

  get linkTarget(): string {
    return this.openInSameWindow() ? TargetUrl.sameWindow : TargetUrl.newWindow;
  }

  ngAfterViewInit(): void {
    if (!this.hasVideo || !this.videoVisibility) {
      return;
    }

    const options: IntersectionObserverInit = {
      rootMargin: '0px',
      threshold: 0.1,
    };

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.videoElement?.nativeElement?.play()?.catch(() => {
          // Handle autoplay restrictions
        });
      } else {
        this.videoElement?.nativeElement?.pause();
      }
    }, options);

    this.observer.observe(this.videoVisibility.nativeElement);
  }

  toggleMute(event: Event): void {
    event.stopPropagation();
    this.isMuted = !this.isMuted;
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.muted = this.isMuted;
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  onPostClick(): void {
    this.navigateToPost.emit();
  }
}
